import { useTranslation } from "react-i18next";
// src/pages/PayablesList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import {
  ArrowsUpDownIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";

import { apiGet } from "../api/client";
import EmptyState from "../components/EmptyState";
import { companyRoute } from "../utils/routeHelpers";
import {
  ListToolbar,
  type MenuItem,
  type SortOption,
} from "../components/ListToolbar";
import { downloadCsv } from "../utils/downloadCsv";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { A, pillClass } from "../styles/appleClasses";
import { formatMoney } from "../utils/formatNumber";
import { Button } from "../components/Button";
import ListTableShell from "../components/lists/ListTableShell";

interface BillDetail {
  bill_id: number;
  bill_number?: string | null;
  purchase_id?: number | null;
  expense_id?: number | null;
  source_type?: string | null;
  source_id?: number | null;
  due_date: string | null;
  amount_due: number;
  status: string;
  vendor_invoice_number?: string | null;
}

interface PayableSummary {
  vendor_id: number;
  name: string;
  total_due: number;
  aging_overdue: number;
  aging_10: number;
  aging_20: number;
  aging_30: number;
  bills: BillDetail[];
}

interface SettlementPayable {
  account_id: number;
  name: string;
  kind: string;
  balance: number;
}

const ROWS_PER_PAGE = 10;
const PREVIEW_MOBILE = 6;
const PREVIEW_DESKTOP = 8;

type SortKey = "total" | "vendor" | "overdue";
type SortDir = "asc" | "desc";
type BillSortKey = "due" | "amount";
type BillSortDir = "asc" | "desc";

function uniqueBills(bills: BillDetail[]) {
  const seen = new Set<number>();
  const out: BillDetail[] = [];
  for (const b of bills || []) {
    if (!seen.has(b.bill_id)) {
      seen.add(b.bill_id);
      out.push(b);
    }
  }
  return out;
}

function sumVendorDue(p: PayableSummary) {
  return uniqueBills(p.bills).reduce((s, b) => s + (b?.amount_due || 0), 0);
}

function sumAllDue(list: PayableSummary[]) {
  const seen = new Set<number>();
  let sum = 0;
  for (const p of list || []) {
    for (const b of p.bills || []) {
      if (!seen.has(b.bill_id)) {
        seen.add(b.bill_id);
        sum += b?.amount_due || 0;
      }
    }
  }
  return sum;
}

function formatDate(d: string | null) {
  return formatAnyDateToLocale(d);
}

function vendorRef(bill: BillDetail) {
  const ref = (bill.vendor_invoice_number || "").trim();
  return ref || "—";
}

function sortBills(
  bills: BillDetail[],
  sort: { key: BillSortKey; dir: BillSortDir },
) {
  const sign = sort.dir === "asc" ? 1 : -1;

  const toTime = (d: string | null) => {
    if (!d) return Number.POSITIVE_INFINITY;
    const t = new Date(d).getTime();
    return isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };

  return bills.slice().sort((a, b) => {
    if (sort.key === "amount") {
      const av = Number(a.amount_due ?? 0);
      const bv = Number(b.amount_due ?? 0);
      if (av !== bv) return (av - bv) * sign;
    } else {
      const ta = toTime(a.due_date);
      const tb = toTime(b.due_date);
      if (ta !== tb) return (ta - tb) * sign;
    }

    const cmpRef = vendorRef(a).localeCompare(vendorRef(b), undefined, {
      sensitivity: "base",
    });
    if (cmpRef !== 0) return cmpRef;
    return (a.bill_id || 0) - (b.bill_id || 0);
  });
}

function csvEscape(v: unknown) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function PayablesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [payables, setPayables] = useState<PayableSummary[]>([]);
  const [settlementPayables, setSettlementPayables] = useState<
    SettlementPayable[]
  >([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [showAllBills, setShowAllBills] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"directory.customers.noCompany" | "payables.loadError" | null>(null);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [billSort, setBillSort] = useState<{
    key: BillSortKey;
    dir: BillSortDir;
  }>({
    key: "due",
    dir: "asc",
  });

  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!company_id) {
      setError("directory.customers.noCompany");
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all([
      apiGet<PayableSummary[]>(`/ar-ap/payables?company_id=${company_id}`),
      apiGet<SettlementPayable[]>(
        `/ar-ap/settlement-payables?company_id=${company_id}`,
      ),
    ])
      .then(([payablesData, settlementData]) => {
        setPayables(payablesData || []);
        setSettlementPayables(settlementData || []);
        setError(null);
      })
      .catch(() => setError("payables.loadError"))
      .finally(() => setLoading(false));
  }, [company_id]);

  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortDir, payables.length]);

  const goToSource = (bill: BillDetail) => {
    if (!company_id) return;

    if (bill.source_type === "purchase" && bill.source_id) {
      navigate(companyRoute(company_id, `/purchases/${bill.source_id}`));
      return;
    }

    if (bill.source_type === "expense" && bill.source_id) {
      navigate(companyRoute(company_id, `/expenses/${bill.source_id}`));
      return;
    }

    if (bill.purchase_id) {
      navigate(companyRoute(company_id, `/purchases/${bill.purchase_id}`));
      return;
    }

    if (bill.expense_id) {
      navigate(companyRoute(company_id, `/expenses/${bill.expense_id}`));
    }
  };

  const sourceLabel = (bill: BillDetail) => {
    if (bill.source_type === "expense" || bill.expense_id)
      return t("expenses.list.viewExpense", { id: bill.source_id || bill.expense_id });
    if (bill.source_type === "purchase" || bill.purchase_id)
      return t("purchases.list.view", { id: bill.source_id || bill.purchase_id });
    return t("payables.viewBill", { id: bill.bill_id });
  };

  const filteredPayables = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return payables;

    return payables.filter((p) => {
      const matchVendor = (p.name || "").toLowerCase().includes(q);
      const matchVendorInvoice = (p.bills || []).some((b) =>
        vendorRef(b).toLowerCase().includes(q),
      );
      return matchVendor || matchVendorInvoice;
    });
  }, [payables, search]);

  const sortedPayables = useMemo(() => {
    const list = filteredPayables.slice();

    list.sort((a, b) => {
      if (sortKey === "vendor") {
        const cmp = (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        });
        return sortDir === "asc" ? cmp : -cmp;
      }

      if (sortKey === "overdue") {
        const diff = (a.aging_overdue || 0) - (b.aging_overdue || 0);
        if (diff !== 0) return sortDir === "asc" ? diff : -diff;

        const t = sumVendorDue(a) - sumVendorDue(b);
        if (t !== 0) return sortDir === "asc" ? t : -t;

        return (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        });
      }

      const diff = sumVendorDue(a) - sumVendorDue(b);
      if (diff !== 0) return sortDir === "asc" ? diff : -diff;

      const od = (a.aging_overdue || 0) - (b.aging_overdue || 0);
      if (od !== 0) return sortDir === "asc" ? od : -od;

      return (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      });
    });

    return list;
  }, [filteredPayables, sortKey, sortDir]);

  const totalFiltered = sortedPayables.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const pagedPayables = sortedPayables.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );

  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = Math.min(page * ROWS_PER_PAGE, totalFiltered);

  const totalPayables = useMemo(
    () => sumAllDue(filteredPayables),
    [filteredPayables],
  );
  const totalSettlementPayables = useMemo(
    () =>
      settlementPayables.reduce((sum, item) => sum + Number(item.balance || 0), 0),
    [settlementPayables],
  );
  const primarySettlementPayable = settlementPayables[0] || null;

  const sortValue = `${sortKey}_${sortDir}`;
  const sortOptions: SortOption[] = [
    { label: t("payables.sort.totalHigh"), value: "total_desc" },
    { label: t("payables.sort.totalLow"), value: "total_asc" },
    { label: t("payables.sort.vendorAsc"), value: "vendor_asc" },
    { label: t("payables.sort.vendorDesc"), value: "vendor_desc" },
    { label: t("receivables.sort.overdueHigh"), value: "overdue_desc" },
    { label: t("receivables.sort.overdueLow"), value: "overdue_asc" },
  ];

  const onToolbarSortChange = (v: string) => {
    const [k, d] = v.split("_") as [SortKey, SortDir];
    setSortKey(k);
    setSortDir(d);
  };

  const onHeaderSort = (k: SortKey) => {
    if (sortKey !== k) {
      setSortKey(k);
      setSortDir(k === "vendor" ? "asc" : "desc");
      return;
    }
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const sortArrow = (k: SortKey) =>
    sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : null;

  const clearFilters = () => {
    setSearch("");
    setSortKey("total");
    setSortDir("desc");
    setExpanded({});
    setShowAllBills({});
  };

  const toggleExpanded = (vendorId: number) => {
    setExpanded((exp) => {
      const nextOpen = !exp[vendorId];

      if (!nextOpen) {
        setShowAllBills((m) => {
          const next = { ...m };
          delete next[vendorId];
          return next;
        });
      }

      return { ...exp, [vendorId]: nextOpen };
    });
  };

  const toggleBillSortDue = () => {
    setBillSort((s) =>
      s.key === "due"
        ? { key: "due", dir: s.dir === "asc" ? "desc" : "asc" }
        : { key: "due", dir: "asc" },
    );
  };

  const toggleBillSortAmount = () => {
    setBillSort((s) =>
      s.key === "amount"
        ? { key: "amount", dir: s.dir === "asc" ? "desc" : "asc" }
        : { key: "amount", dir: "desc" },
    );
  };

  const exportCsv = () => {
    const rows = sortedPayables.map((p) => {
      const bills = uniqueBills(p.bills);
      const totalDue = sumVendorDue(p);

      return {
        vendor: p.name || "",
        overdue: p.aging_overdue || 0,
        next10d: p.aging_10 || 0,
        d11_20: p.aging_20 || 0,
        d21_30: p.aging_30 || 0,
        total_due: totalDue,
        bill_count: bills.length,
      };
    });

    const header = [
      "Vendor",
      "Overdue",
      "10D",
      "20D",
      "30D",
      "Total",
      "Bill Count",
    ];
    const lines = [
      header.join(","),
      ...rows.map((r) =>
        [
          csvEscape(r.vendor),
          r.overdue.toFixed(2),
          r.next10d.toFixed(2),
          r.d11_20.toFixed(2),
          r.d21_30.toFixed(2),
          r.total_due.toFixed(2),
          String(r.bill_count),
        ].join(","),
      ),
    ];

    downloadCsv("payables", lines.join("\n"));
  };

  const newItems: MenuItem[] = company_id
    ? [
        {
          label: t("purchases.common.new"),
          onClick: () => navigate(companyRoute(company_id, "/purchases/new")),
        },
      ]
    : [];

  const moreItems: MenuItem[] = [{ label: t("directory.common.exportCsv"), onClick: exportCsv }];

  const PAGE_W = "max-w-5xl";
  const money = (n: number | null | undefined) =>
    `${formatMoney(Number(n ?? 0))}`;

  const iconBtnCls = [
    A.iconBtn,
    "inline-flex items-center justify-center",
    "hover:bg-gray-100",
    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
  ].join(" ");

  const iconBtnSmCls = [
    "h-9 w-9 rounded-xl",
    "inline-flex items-center justify-center",
    "hover:bg-gray-100",
    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
  ].join(" ");

  const linkCls = "text-[13px] font-normal text-appleblue hover:text-appleblueD";

  const sortPillBase = [
    A.pillBase,
    "transition",
    "hover:bg-gray-50",
  ].join(" ");
  const sortPillCls = (active: boolean) =>
    [
      sortPillBase,
      active
        ? "bg-white text-gray-700 ring-black/10"
        : "bg-transparent text-gray-500 ring-black/5",
    ].join(" ");

  const statusPill = (statusRaw: string | null | undefined) => {
    const s = (statusRaw || "").toLowerCase();
    if (s === "paid") return { label: t("purchases.status.paid"), cls: pillClass("success") };
    if (s === "open") return { label: t("payables.status.open"), cls: pillClass("warning") };
    if (s === "due soon") return { label: t("payables.status.dueSoon"), cls: pillClass("neutral") };
    if (s === "closed") return { label: t("purchases.status.closed"), cls: pillClass("neutral") };
    if (s === "overdue")
      return {
        label: t("purchases.status.overdue"),
        cls: `${A.pillBase} bg-red-50 text-red-700 ring-red-100`,
      };
    return { label: statusRaw || "—", cls: pillClass("neutral") };
  };

  const pagerText = (
    <span className={`${A.label} text-gray-500 select-none`}>
      {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
    </span>
  );

  const pagerBtnExtraCls = "px-2 active:scale-100";

  if (loading) {
    return (
      <div className={`text-center py-6 text-gray-600 ${A.body}`}>
        {t("payables.loading")}
      </div>
    );
  }

  if (error) return <EmptyState message={t(error)} heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />;
  if (!company_id) return <EmptyState message={t("directory.customers.noCompany")} heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />;

  if (!payables.length && totalSettlementPayables <= 0) {
    return (
      <EmptyState
        message={t("payables.empty")}
        heading={t("directory.common.nothingHere")}
        backLabel={t("directory.common.goBack")}
        showNewButton
        newButtonText={"+ " + t("purchases.common.new")}
        newButtonPath={companyRoute(company_id, "/purchases/new")}
      />
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 ${A.body}`}
    >
      <div
        className={[
          "sticky top-0 z-30 bg-gray-50 w-full",
          PAGE_W,
          "flex items-center border-b border-gray-100 px-2 pb-2 mb-2",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtnCls}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`flex-1 text-center tracking-tight ${A.title}`}>
          {t("navigation.payables")}
        </h2>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtnCls}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      <div className={`w-full ${PAGE_W} px-2 mb-4 md:hidden`}>
        <div className="relative w-full">
          <span className="absolute left-3 top-3 text-gray-400 pointer-events-none">
            <svg
              width="18"
              height="18"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                d="M20 20l-3-3"
              />
            </svg>
          </span>

          <input
            type="text"
            placeholder={t("payables.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${A.inputBase} pl-9 pr-4 placeholder-gray-400 appearance-none`}
          />
        </div>

        <div className="mt-3 flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            minWidth="auto"
            className="flex-1"
            onClick={() => navigate(companyRoute(company_id, "/purchases/new"))}
          >
            {t("purchases.common.new")}
          </Button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMobileMoreOpen((open) => !open)}
              className={`${A.iconBtn} w-12 inline-flex items-center justify-center border border-gray-200 bg-white hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue`}
              aria-label={t("directory.common.more")}
              aria-haspopup="menu"
              aria-expanded={mobileMoreOpen}
            >
              <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
            </button>

            {mobileMoreOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 rounded-2xl border border-gray-200 bg-white shadow-md p-1 z-50 w-max min-w-[10rem] max-w-[90vw]"
                onClick={() => setMobileMoreOpen(false)}
              >
                {moreItems.map((item, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`${A.body} w-full text-left px-3 py-2 rounded-xl hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-appleblueD transition`}
                    onClick={item.onClick}
                    disabled={item.disabled}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-end gap-2">
          <div className="relative flex-1 min-w-0">
            <label className="sr-only" htmlFor="payables-mobile-sort">
              {t("directory.common.sort")}
            </label>
            <select
              id="payables-mobile-sort"
              value={sortValue}
              onChange={(e) => onToolbarSortChange(e.target.value)}
              className={`${A.inputBase} pr-10 appearance-none`}
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <ChevronDownIcon className="w-4 h-4" />
            </span>
          </div>

          <div className="text-right leading-tight shrink-0">
            <div className={A.label}>{t("payables.totalPayables")}</div>
            <div className={`text-[13px] font-semibold text-gray-900 ${A.num}`}>
              {money(totalPayables + totalSettlementPayables)}
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:block w-full">
        <ListToolbar
          maxWidthClass={PAGE_W}
          moreLabel={t("directory.common.more")}
          sortLabel={t("directory.common.sort")}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("payables.search")}
          sortValue={sortValue}
          sortOptions={sortOptions}
          onSortChange={onToolbarSortChange}
          showDateRange={false}
          newItems={newItems}
          moreItems={moreItems}
          rightSummary={
            <div className="text-right leading-tight">
              <div className={A.label}>{t("payables.totalPayables")}</div>
              <div className={`text-[13px] font-semibold text-gray-900 ${A.num}`}>
                {money(totalPayables + totalSettlementPayables)}
              </div>
            </div>
          }
        />
      </div>

      {payables.length > 0 && totalFiltered === 0 && !primarySettlementPayable ? (
        <div className={`w-full ${PAGE_W} px-2`}>
          <div className={`${A.card} p-6 text-center`}>
            <div className="text-[13px] text-gray-600">{t("directory.common.noResults")}</div>
            <button
              type="button"
              onClick={clearFilters}
              className={`ml-2 ${linkCls}`}
            >
              {t("directory.common.clearFilters")}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={`w-full ${PAGE_W} px-2 grid gap-3 md:hidden`}>
            {primarySettlementPayable && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    navigate(companyRoute(company_id, "/payables/credit-card"))
                  }
                  className={`${A.card} p-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-gray-900 truncate">
                      {t("payables.creditCardPayable")}
                    </div>
                    <div
                      className={`ml-auto font-semibold text-gray-900 ${A.num}`}
                    >
                      {money(primarySettlementPayable.balance)}
                    </div>
                  </div>
                </button>

                {pagedPayables.length > 0 && (
                  <div className="h-px bg-gray-200 mx-1" />
                )}
              </>
            )}

            {pagedPayables.map((pay) => {
              const vendorTotal = sumVendorDue(pay);
              const bills = sortBills(uniqueBills(pay.bills), billSort);
              const showAll = !!showAllBills[pay.vendor_id];
              const displayBills = showAll
                ? bills
                : bills.slice(0, PREVIEW_MOBILE);

              return (
                <div
                  key={pay.vendor_id}
                  className={`${A.card} min-w-0 p-4 flex flex-col`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900 truncate">
                      {pay.name}
                    </div>

                    <button
                      className={iconBtnSmCls}
                      onClick={() => toggleExpanded(pay.vendor_id)}
                      aria-label={t("payables.toggleBills")}
                      type="button"
                    >
                      {expanded[pay.vendor_id] ? (
                        <ChevronDownIcon className="w-5 h-5 text-appleblue" />
                      ) : (
                        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                    <div>
                      {t("purchases.status.overdue")}:{" "}
                      <span className={`font-medium text-gray-800 ${A.num}`}>
                        {pay.aging_overdue > 0 ? money(pay.aging_overdue) : "—"}
                      </span>
                    </div>
                    <div>
                      {t("receivables.aging10")}:{" "}
                      <span className={`font-medium text-gray-800 ${A.num}`}>
                        {pay.aging_10 > 0 ? money(pay.aging_10) : "—"}
                      </span>
                    </div>
                    <div>
                      {t("receivables.aging20")}:{" "}
                      <span className={`font-medium text-gray-800 ${A.num}`}>
                        {pay.aging_20 > 0 ? money(pay.aging_20) : "—"}
                      </span>
                    </div>
                    <div>
                      {t("receivables.aging30")}:{" "}
                      <span className={`font-medium text-gray-800 ${A.num}`}>
                        {pay.aging_30 > 0 ? money(pay.aging_30) : "—"}
                      </span>
                    </div>

                    <div
                      className={`ml-auto font-semibold text-gray-900 ${A.num}`}
                    >
                      {money(vendorTotal)}
                    </div>
                  </div>

                  {expanded[pay.vendor_id] && (
                    <div className="mt-3 border-t border-gray-100 pt-2 max-h-[18rem] overflow-auto">
                      <div className="mb-2">
                        <div className={A.label}>{t("payables.outstandingBills")}</div>

                        <div className="mt-2 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={toggleBillSortDue}
                            className={sortPillCls(billSort.key === "due")}
                            title={t("receivables.sortDueDate")}
                          >
                            {t("purchases.common.dueShort")}{" "}
                            <span className="ml-1 text-gray-400">
                              {billSort.key === "due"
                                ? billSort.dir === "asc"
                                  ? "↑"
                                  : "↓"
                                : (
                                  <ArrowsUpDownIcon
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                    focusable="false"
                                  />
                                )}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={toggleBillSortAmount}
                            className={sortPillCls(billSort.key === "amount")}
                            title={t("payables.sortAmountDue")}
                          >
                            {t("sales.common.amount")}{" "}
                            <span className="ml-1 text-gray-400">
                              {billSort.key === "amount"
                                ? billSort.dir === "asc"
                                  ? "↑"
                                  : "↓"
                                : (
                                  <ArrowsUpDownIcon
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                    focusable="false"
                                  />
                                )}
                            </span>
                          </button>
                        </div>
                      </div>

                      {bills.length ? (
                        <>
                          {displayBills.map((bill) => {
                            const pill = statusPill(bill.status);
                            return (
                              <div
                                key={bill.bill_id}
                                className={[
                                  "py-2 flex items-start justify-between border-b border-gray-100 last:border-none",
                                  "cursor-pointer hover:bg-gray-50 rounded-xl",
                                ].join(" ")}
                                onClick={() => goToSource(bill)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") goToSource(bill);
                                }}
                                aria-label={sourceLabel(bill)}
                              >
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-800 whitespace-normal [overflow-wrap:anywhere]">
                                    {vendorRef(bill)}
                                  </div>
                                  <div className={`${A.label} text-gray-500`}>
                                    {formatDate(bill.due_date)}
                                  </div>
                                </div>

                                <div className="flex flex-col items-end">
                                  <div
                                    className={`font-medium text-gray-900 ${A.num}`}
                                  >
                                    {money(bill.amount_due)}
                                  </div>
                                  <span className={`mt-1 ${pill.cls}`}>
                                    {pill.label}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          {bills.length > PREVIEW_MOBILE ? (
                            <button
                              type="button"
                              onClick={() =>
                                setShowAllBills((m) => ({
                                  ...m,
                                  [pay.vendor_id]: !showAll,
                                }))
                              }
                              className={`mt-2 ${linkCls}`}
                            >
                              {showAll
                                ? t("receivables.showLess")
                                : t("receivables.showAll", { count: bills.length })}
                            </button>
                          ) : null}
                        </>
                      ) : (
                        <div className="text-[13px] text-gray-500">
                          {t("payables.noBills")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            className={`flex md:hidden justify-between items-center mt-4 w-full ${PAGE_W} px-2`}
          >
            {pagerText}
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnExtraCls}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                type="button"
              >
                {t("directory.common.prev")}
              </Button>
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnExtraCls}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                type="button"
              >
                {t("directory.common.next")}
              </Button>
            </div>
          </div>

          <div className={`hidden md:block w-full ${PAGE_W} mx-auto px-2 mt-3`}>
            <ListTableShell>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b">
                    <th className="w-8" />
                    <th className="py-2 text-left text-xs text-gray-500">
                      <button
                        type="button"
                        onClick={() => onHeaderSort("vendor")}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                        title={t("payables.sortPayable")}
                      >
                        {t("payables.payable")} {sortArrow("vendor")}
                      </button>
                    </th>
                    <th className="py-2 text-right text-xs text-gray-500">
                      <button
                        type="button"
                        onClick={() => onHeaderSort("overdue")}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                        title={t("receivables.sortOverdue")}
                      >
                        {t("purchases.status.overdue")} {sortArrow("overdue")}
                      </button>
                    </th>
                    <th className="py-2 text-right text-xs text-gray-500">
                      {t("receivables.aging10")}
                    </th>
                    <th className="py-2 text-right text-xs text-gray-500">
                      {t("receivables.aging20")}
                    </th>
                    <th className="py-2 text-right text-xs text-gray-500">
                      {t("receivables.aging30")}
                    </th>
                    <th className="py-2 pl-4 pr-6 text-right text-xs text-gray-500">
                      <button
                        type="button"
                        onClick={() => onHeaderSort("total")}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        title={t("payables.sortTotalDue")}
                      >
                        {t("sales.common.total")} {sortArrow("total")}
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {primarySettlementPayable && (
                    <tr
                      className="border-b border-gray-200 hover:bg-gray-50 text-gray-700 transition cursor-pointer"
                      onClick={() =>
                        navigate(companyRoute(company_id, "/payables/credit-card"))
                      }
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          navigate(
                            companyRoute(company_id, "/payables/credit-card"),
                          );
                        }
                      }}
                    >
                      <td className="py-2" />
                      <td className="py-2 max-w-[14rem] truncate font-medium text-gray-900">
                        {t("payables.creditCardPayable")}
                      </td>
                      <td className={`py-2 text-right font-medium ${A.num}`}>
                        —
                      </td>
                      <td className={`py-2 text-right font-medium ${A.num}`}>
                        —
                      </td>
                      <td className={`py-2 text-right font-medium ${A.num}`}>
                        —
                      </td>
                      <td className={`py-2 text-right font-medium ${A.num}`}>
                        —
                      </td>
                      <td
                        className={`py-2 pl-4 pr-6 text-right font-semibold text-gray-900 ${A.num}`}
                      >
                        {money(primarySettlementPayable.balance)}
                      </td>
                    </tr>
                  )}

                  {pagedPayables.map((pay) => {
                    const vendorTotal = sumVendorDue(pay);
                    const bills = sortBills(uniqueBills(pay.bills), billSort);
                    const showAll = !!showAllBills[pay.vendor_id];
                    const displayBills = showAll
                      ? bills
                      : bills.slice(0, PREVIEW_DESKTOP);

                    return (
                      <React.Fragment key={pay.vendor_id}>
                        <tr
                          className="border-b hover:bg-gray-50 text-gray-700 transition cursor-pointer"
                          onClick={() => toggleExpanded(pay.vendor_id)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              toggleExpanded(pay.vendor_id);
                          }}
                        >
                          <td className="py-2">
                            {expanded[pay.vendor_id] ? (
                              <ChevronDownIcon className="w-4 h-4 text-appleblue" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                            )}
                          </td>
                          <td className="py-2 max-w-[14rem] truncate font-medium text-gray-900">
                            {pay.name}
                          </td>
                          <td
                            className={`py-2 text-right font-medium ${A.num}`}
                          >
                            {pay.aging_overdue > 0
                              ? money(pay.aging_overdue)
                              : "—"}
                          </td>
                          <td
                            className={`py-2 text-right font-medium ${A.num}`}
                          >
                            {pay.aging_10 > 0 ? money(pay.aging_10) : "—"}
                          </td>
                          <td
                            className={`py-2 text-right font-medium ${A.num}`}
                          >
                            {pay.aging_20 > 0 ? money(pay.aging_20) : "—"}
                          </td>
                          <td
                            className={`py-2 text-right font-medium ${A.num}`}
                          >
                            {pay.aging_30 > 0 ? money(pay.aging_30) : "—"}
                          </td>
                          <td
                            className={`py-2 pl-4 pr-6 text-right font-semibold text-gray-900 ${A.num}`}
                          >
                            {money(vendorTotal)}
                          </td>
                        </tr>

                        {expanded[pay.vendor_id] && bills.length > 0 && (
                          <tr className="bg-gray-50">
                            <td />
                            <td colSpan={6} className="p-0">
                              <div className="pl-2 py-3">
                                <div className="max-h-[22rem] overflow-auto rounded-xl">
                                  <table className="w-full table-fixed text-[13px]">
                                    <thead>
                                      <tr className="text-xs text-gray-500">
                                        <th className="w-1/4 text-left py-1">
                                          {t("payables.billRef")}
                                        </th>
                                        <th className="w-1/4 text-left py-1">
                                          <button
                                            type="button"
                                            onClick={toggleBillSortDue}
                                            className="inline-flex items-center gap-1 hover:text-gray-700"
                                            title={t("receivables.toggleDueDateSort")}
                                          >
                                            {t("sales.common.dueDate")}{" "}
                                            {billSort.key === "due" ? (
                                              <span className="text-[11px] text-gray-400">
                                                {billSort.dir === "asc"
                                                  ? "↑"
                                                  : "↓"}
                                              </span>
                                            ) : (
                                              <span className="text-[11px] text-gray-300">
                                                ↕
                                              </span>
                                            )}
                                          </button>
                                        </th>
                                        <th className="w-1/4 text-right py-1 pl-4 pr-6">
                                          <button
                                            type="button"
                                            onClick={toggleBillSortAmount}
                                            className="inline-flex items-center gap-1 hover:text-gray-700"
                                            title={t("payables.toggleAmountDueSort")}
                                          >
                                            {t("payables.amountDue")}{" "}
                                            {billSort.key === "amount" ? (
                                              <span className="text-[11px] text-gray-400">
                                                {billSort.dir === "asc"
                                                  ? "↑"
                                                  : "↓"}
                                              </span>
                                            ) : (
                                              <span className="text-[11px] text-gray-300">
                                                ↕
                                              </span>
                                            )}
                                          </button>
                                        </th>
                                        <th className="w-1/4 text-center py-1 pl-4 pr-6">
                                          {t("sales.common.status")}
                                        </th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {displayBills.map((bill) => {
                                        const pill = statusPill(bill.status);
                                        return (
                                          <tr
                                            key={bill.bill_id}
                                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                            onClick={() => goToSource(bill)}
                                          >
                                            <td className="py-2 pr-2 font-medium text-gray-800">
                                              {vendorRef(bill)}
                                            </td>
                                            <td className="py-2 pr-2 text-gray-600">
                                              {formatDate(bill.due_date)}
                                            </td>
                                            <td
                                              className={`py-2 pl-4 pr-6 text-right font-medium text-gray-900 ${A.num}`}
                                            >
                                              {money(bill.amount_due)}
                                            </td>
                                            <td className="py-2 pl-4 pr-6 text-center">
                                              <span className={pill.cls}>
                                                {pill.label}
                                              </span>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>

                                {bills.length > PREVIEW_DESKTOP ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowAllBills((m) => ({
                                        ...m,
                                        [pay.vendor_id]: !showAll,
                                      }))
                                    }
                                    className={`mt-2 ${linkCls}`}
                                  >
                                    {showAll
                                      ? t("receivables.showLess")
                                      : t("receivables.showAll", { count: bills.length })}
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </ListTableShell>

            <div className="hidden md:flex justify-between items-center mt-4">
              {pagerText}
              <div className="flex gap-2">
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className={pagerBtnExtraCls}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  type="button"
                >
                  {t("directory.common.prev")}
                </Button>
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className={pagerBtnExtraCls}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  type="button"
                >
                  {t("directory.common.next")}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
